import Cookies from 'universal-cookie';
import jwt_decode from 'jwt-decode';
import {
  API_AUTH_LOGIN_REQUEST,
  API_AUTH_LOGIN_SUCCESS,
  API_AUTH_LOGIN_FAILURE,
  API_AUTH_LOGOUT_REQUEST,
  API_AUTH_LOGOUT_SUCCESS,
  API_AUTH_LOGOUT_FAILURE,
  API_AUTH_REGISTER_REQUEST,
  API_AUTH_REGISTER_SUCCESS,
  API_AUTH_REGISTER_FAILURE,
  API_AUTH_REFRESH_SUCCESS,
  API_AUTH_REFRESH_FAILURE,
  API_AUTH_REFRESH_REQUEST,
  LOGOUT,
  TOGGLE_TIMEOUT_MODAL,
  TOGGLE_PROFILE_UPDATE_MODAL,
  API_AUTH_TOKEN_LOADING_COMPLETE,
  AUTH_HANDLE_PROFILE,
} from 'common/types';
import _ from 'lodash';
import {TOKEN_LOADED} from 'app-ui-componentlibrary/dist/common/constants';

const initialState = {
  error: null,
  token: null,
  secretMessage: null,
  lastUpdated: null,
  user: null,
  isTimeoutModalActive: false,
};

const cookies = new Cookies();

// ------------------------------------
// Action Handlers
// ------------------------------------
const ACTION_HANDLERS = {
  [API_AUTH_LOGIN_REQUEST]: (state) => {
    cookies.remove('token');
    return {...state, token: null, error: null};
  },
  [API_AUTH_LOGIN_SUCCESS]: (state, action) => {
    const token = _.get(action, 'payload.token');
    var decoded = null;
    if (token) {
      try {
        decoded = jwt_decode(token);
      } catch (e) {}
    }

    var sessionInfo = {};
    //get xsrf token icam from requests cookies
    const _csrftoken = getRefreshToken();
    //decoded is null when refresh browser triggers refresh from TokenGate which directs the call here without payload.token
    if (_csrftoken) {
      if (!decoded) {
        try {
          decoded = jwt_decode(_csrftoken);
        } catch (e) {}
      }
      sessionInfo.iat = decoded.iat;
      sessionInfo.exp = decoded.exp;
      sessionInfo.userId = decoded.username;
      sessionInfo.permissions = decoded.permissions;
      sessionInfo._csrftoken = getIcamSRFToken();
    }
    decoded.generalPermissions = _.get(action, 'payload.generalPermissions');
    const {username: userId, ...rest} = decoded;
    const newDecoded = {userId, ...rest};

    // Datadog is optional: until datadog-logs.js loads, or for good if it is blocked,
    // DD_LOGS is the stub from public/index.html, which has onReady but no other methods.
    try {
      window.DD_LOGS?.onReady?.(() => {
        window.DD_LOGS.setGlobalContextProperty('usr', {
          id: userId,
          permissions: decoded.permissions,
        });
      });
    } catch (e) {
      console.warn('Datadog unavailable; user tag skipped');
    }

    return {
      ...newDecoded,
      status: TOKEN_LOADED,
      token: sessionInfo,
      error: null,
    };
  },
  [API_AUTH_LOGIN_FAILURE]: (state, action) => {
    cookies.remove('token');
    return {
      token: null,
      status: TOKEN_LOADED,
      error: _.get(action, 'payload.response.message'),
    };
  },
  [API_AUTH_LOGOUT_REQUEST]: (state) => {
    cookies.remove('token');
    try {
      window.DD_LOGS?.onReady?.(() => {
        window.DD_LOGS.removeGlobalContextProperty('usr');
      });
    } catch (e) {
      console.warn('Datadog unavailable; user tag not cleared');
    }
    return {...state, token: null, error: null};
  },
  [API_AUTH_LOGOUT_SUCCESS]: (state, action) => {
    return {
      status: TOKEN_LOADED,
      token: null,
      error: null,
    };
  },
  [API_AUTH_LOGOUT_FAILURE]: (state, action) => {
    return {
      token: null,
      status: TOKEN_LOADED,
      error: _.get(action, 'payload.response.message'),
    };
  },
  [API_AUTH_REGISTER_REQUEST]: (state) => {
    return {...state};
  },
  [API_AUTH_REGISTER_SUCCESS]: (state, action) => {
    return {
      ...state,
    };
  },
  [API_AUTH_REGISTER_FAILURE]: (state, action) => {
    cookies.remove('token');
    return {
      ...state,
    };
  },
  [API_AUTH_REFRESH_REQUEST]: (state, action) => {
    return {
      ...state,
    };
  },
  [API_AUTH_REFRESH_SUCCESS]: (state, action) => {
    const _csrftoken = getRefreshToken();
    if (!_csrftoken) {
      //redirect to login if token is not in storage
      return {...state, token: null, error: null};
    }
    var decoded = null;
    if (_csrftoken) {
      try {
        decoded = jwt_decode(_csrftoken);
      } catch (e) {}
    }
    var sessionInfo = {};
    if (decoded) {
      //overwrite permissions from CSRF token with actual user permissions on payload
      decoded.permissions = _.get(action, 'payload.permissions');
      decoded.generalPermissions = _.get(action, 'payload.generalPermissions');
      sessionInfo.iat = decoded.iat;
      sessionInfo.exp = decoded.exp;
      sessionInfo.userId = decoded.username;
      sessionInfo._csrftoken = getIcamSRFToken();
      sessionInfo.permissions = decoded.permissions;
    }
    const {username: userId, ...rest} = decoded;
    const newDecoded = {userId, ...rest};
    return {
      ...state,
      ...newDecoded,
      status: TOKEN_LOADED,
      token: sessionInfo,
      error: null,
    };
  },
  [API_AUTH_REFRESH_FAILURE]: (state, action) => {
    cookies.remove('token');
    return {
      token: null,
      status: TOKEN_LOADED,
      error: _.get(action, 'payload.response.message'),
    };
  },
  [LOGOUT]: (state, action) => {
    return {
      ...state,
      status: TOKEN_LOADED,
      isTimeoutModalActive: false, // Make sure timeout modal is disabled on logout
    };
  },
  [TOGGLE_TIMEOUT_MODAL]: (state, action) => {
    return {
      ...state,
      isTimeoutModalActive: action.payload,
    };
  },
  [TOGGLE_PROFILE_UPDATE_MODAL]: (state, action) => {
    //used to notify users of profile changes
    cookies.remove('bannerReport');
    return {
      ...state,
    };
  },
  // used just for setting the token loading as false when there's no token to load
  [API_AUTH_TOKEN_LOADING_COMPLETE]: (state, action) => {
    return {
      ...state,
      status: TOKEN_LOADED,
    };
  },
  [AUTH_HANDLE_PROFILE]: (state, action) => {
    const firstName = action.payload.firstName;
    const lastName = action.payload.lastName;
    return {
      ...state,
      firstName: firstName,
      lastName: lastName,
    };
  },
};

// ------------------------------------
// Reducer
// ------------------------------------
export default (state = initialState, action) => {
  const handler = ACTION_HANDLERS[action.type];
  return handler ? handler(state, action) : state;
};

const getIcamSRFToken = () => {
  var cookies = document.cookie.split(';');
  const cookieName = 'XSRF-TOKEN-';
  let _csrftokens = {};
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(cookieName) && cookie.indexOf('=') > -1) {
      const csrf = cookie.split('=');
      _.set(
        _csrftokens,
        'X-' + _.get(csrf, 0, cookieName),
        decodeURIComponent(_.get(csrf, 1))
      );
    }
  }
  return _csrftokens['X-XSRF-TOKEN-icam'];
};

export const getRefreshToken = () => {
  var cookies = document.cookie.split(';');
  const cookieName = 'token';
  let _csrftokens = {};
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    if (cookie.startsWith(cookieName) && cookie.indexOf('=') > -1) {
      const csrf = cookie.split('=');
      _.set(_csrftokens, _.get(csrf, 0, cookieName), decodeURIComponent(_.get(csrf, 1)));
    }
  }
  return _csrftokens['token'];
};
